import type { Layer } from "@deck.gl/core"
import { useAppSelector } from "integrations/redux/hooks"
import {
    useWeatherHeatmapOpacity,
    selectWeatherPanel,
    selectWeatherTimeline,
} from "features/map/control/selectors"
import { WindParticleLayer } from "./WindParticleLayer"
import { createNoCoverageLayer } from "../shared/NoCoverageLayer"
import { createTileSource } from "../series/weatherTileSource"
import { useDisplayFrame, useWeatherSeries } from "../series/weatherSeries"

export type WindLayers = {
    // Single layer renders heatmap + trails together so trails get true additive blend against the
    // heatmap (see WindParticleLayer). Occupies the heatmap z-slot.
    heatmap: Layer | null
    particles: Layer | null
}

const source = createTileSource("wind10m")

export const useWindLayers = (): WindLayers => {
    const series = useWeatherSeries("wind", source)
    const { currentTime } = useAppSelector(selectWeatherTimeline)
    const { particleEffect } = useAppSelector(selectWeatherPanel)
    const heatmapOpacity = useWeatherHeatmapOpacity()
    const frame = useDisplayFrame("wind", series, currentTime)

    if (!series) return { heatmap: null, particles: null }

    if (!series.hasCoverage) {
        return {
            heatmap: createNoCoverageLayer(series.bounds, "WindNoCoverageLayer"),
            particles: null,
        }
    }

    if (!frame) return { heatmap: null, particles: null }

    return {
        heatmap: new WindParticleLayer({
            id: "WindParticleLayer",
            imageA: frame.imageA,
            imageB: frame.imageB,
            imageWeight: frame.imageWeight,
            bounds: frame.bounds,
            heatmapOpacity,
            showParticles: particleEffect,
        }),
        particles: null,
    }
}
